import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

const iconOptions = [
  '📚', '🔬', '🧮', '🌍', '🎨', '💻', '📐', '🎵', '⚽', '🧬', '📝', '🔢', '🏛️', '⚗️', '🧪', '⚛️'
];

export function SubjectModal({ open, onClose, onSave, subject }) {
  const [formData, setFormData] = useState({
    name: '',
    icon: '📚',
    groups: [],
    order: 0,
  });
  const [groupInput, setGroupInput] = useState('');

  useEffect(() => {
    if (subject) {
      setFormData({
        name: subject.name || '',
        icon: subject.icon || '📚',
        groups: subject.groups || [],
        order: subject.order || 0,
      });
    } else {
      setFormData({ name: '', icon: '📚', groups: [], order: 0 });
    }
  }, [subject, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const addGroup = () => {
    const num = parseInt(groupInput);
    if (!isNaN(num) && !formData.groups.includes(num)) {
      setFormData({ ...formData, groups: [...formData.groups, num] });
      setGroupInput('');
    }
  };

  const removeGroup = (group) => {
    setFormData({ ...formData, groups: formData.groups.filter(g => g !== group) });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="subject-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            {subject ? 'Dersi Düzenle' : 'Yeni Ders Ekle'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Ders Adı</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Örn: Matematik"
              required
              data-testid="subject-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>İkon</Label>
            <div className="flex flex-wrap gap-2">
              {iconOptions.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => setFormData({ ...formData, icon })}
                  className={`w-10 h-10 text-xl rounded-lg border-2 transition-colors ${
                    formData.icon === icon
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`icon-${icon}`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Gruplar (Sınıflar)</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                value={groupInput}
                onChange={(e) => setGroupInput(e.target.value)}
                placeholder="Grup numarası"
                className="flex-1"
                data-testid="group-input"
              />
              <Button type="button" variant="secondary" onClick={addGroup}>
                Ekle
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.groups.map((group) => (
                <Badge key={group} variant="secondary" className="gap-1">
                  {group}. Sınıf
                  <button type="button" onClick={() => removeGroup(group)}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="order">Sıra</Label>
            <Input
              id="order"
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              data-testid="subject-order-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              İptal
            </Button>
            <Button 
              type="submit" 
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="subject-save-btn"
            >
              Kaydet
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
